import {
  ElementController,
  getData,
  httpGet,
  insertAfter,
  insertBefore,
  parseHTML,
  selectSingleFrom,
  setData,
} from 'js-common';

const ERROR_CLASS_NAME = 'load-more-error';

/**
 * Controller for loading more articles via Ajax.
 *
 * @extends {ElementController}
 */
export default class LoadMore extends ElementController {
  static id = 'LoadMore';

  /**
   * DOM loaded, get nodes and bind events.
   */
  init() {
    this.itemList = this.getSingleNode('item-list');
    this.loadTrigger = this.getSingleNode('load-trigger');
    this.totalNumPages = getData(this.loadTrigger, 'total-pages');
    if (!this.totalNumPages) {
      throw new Error('Missing total number of pages to load');
    }

    this.on(this.loadTrigger, 'click', this.loadPage);
  }

  /**
   * Load the next page and append the response to the list.
   */
  loadPage = async () => {
    const pageQueryVar = getData(this.loadTrigger, 'page-query-var');
    const currentPage = getData(this.loadTrigger, 'current-page');
    const nextPage = currentPage + 1;

    try {
      this.clearLoadingError();
      this.setLoadingState();

      // Load and append
      const result = await httpGet(`?${pageQueryVar}=${nextPage}`);
      insertAfter(this.itemList.lastChild, parseHTML(result.trim()));

      // Remove load button if there are no more pages, update its state
      // otherwise
      if (nextPage === this.totalNumPages) {
        this.loadTrigger.remove();
      } else {
        setData(this.loadTrigger, 'current-page', nextPage);
        this.unsetLoadingState();
      }
    } catch (err) {
      this.unsetLoadingState();
      this.setLoadingError(`Error: ${err.message}`);
      // Will be removed on build
      // eslint-disable-next-line no-console
      console.log(err);
      // Let Sentry see the error
      throw err;
    }
  };

  /**
   * Set a 'currently loading' state.
   */
  setLoadingState = () => {
    this.loadTrigger.disabled = true;
    insertAfter(
      this.loadTrigger.lastChild,
      parseHTML('<span class="spinner spinner--red"></span>'),
    );
  };

  /**
   * Remove a 'currently loading' state.
   */
  unsetLoadingState = () => {
    this.loadTrigger.disabled = false;
    const spinner = selectSingleFrom(this.loadTrigger, '.spinner');
    if (spinner) {
      spinner.remove();
    }
  };

  /**
   * Add an error message for failed loading.
   *
   * @param {string} message - Error message text.
   */
  setLoadingError = (message) => {
    insertBefore(
      this.loadTrigger,
      parseHTML(
        `<div class="message-box message-box--error ${ERROR_CLASS_NAME}"><p>${message}</p></div>`,
      ),
    );
  };

  /**
   * Remove a loading error message.
   */
  clearLoadingError = () => {
    const error = selectSingleFrom(this.getRoot(), `.${ERROR_CLASS_NAME}`);
    if (error) {
      error.remove();
    }
  };
}
